import { generateAccessName, AccessKey } from "../../../../access";

/**
 * Base keys of the access sections on this page.
 */
const ACCESS_SECTION: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.WIDGET_ORDER_PARKING,
];

const ACCESS_SECTION_FILTER: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.WIDGET_ORDER_PARKING,
	AccessKey.FILTER,
];

/**
 * Enum representing special keys for each tab on this page.
 * These keys are used to uniquely identify and manage individual tabs.
 */

enum TabKeys {
	CONTROL_BUTTON_ADD = AccessKey.CONTROL_BUTTON_ADD,
	CONTROL_BUTTON_EDIT = AccessKey.CONTROL_BUTTON_EDIT,
	CONTROL_BUTTON_DELETE = AccessKey.CONTROL_BUTTON_DELETE,
	SEARCH = AccessKey.SEARCH,
	FILTER = AccessKey.FILTER,
	LOWERS_STATISTICS_TABLE = AccessKey.LOWERS_STATISTICS_TABLE,
	//
	COMPANIES_AND_TAXI_SERVICES = AccessKey.COMPANIES_AND_TAXI_SERVICES,
	EXECUTOR_TEAMS = AccessKey.EXECUTOR_TEAMS,
	PARKING_GROUP_LIST = AccessKey.PARKING_GROUP_LIST,
	STATUS = AccessKey.STATUS,
	IS_WORKING = AccessKey.IS_WORKING,
}

/**
 * Constructs an array of access addresses by combining base access addresses
 * with the tab-specific access keys. This builds the full set of addresses
 * needed to manage access from the page level to the specific tab.
 *
 * @param key - Tab-specific access keys.
 * @returns Array of access addresses including base and tab-specific addresses.
 */
const generateAccessSection = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION,
	key,
];
const generateAccessSectionFilter = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION_FILTER,
	key,
];

/**
 * Maps each tab on the page to its corresponding array of access addresses.
 * This container is used for managing access control not only for the tabs themselves,
 * but also for internal elements such as buttons within these tabs.
 * Each entry provides a set of access addresses specific to the tab.
 */
const ARR_ACCESS_PART_KEY: Record<TabKeys, AccessKey[]> = {
	[TabKeys.CONTROL_BUTTON_ADD]: generateAccessSection(
		AccessKey.CONTROL_BUTTON_ADD,
	),
	[TabKeys.CONTROL_BUTTON_EDIT]: generateAccessSection(
		AccessKey.CONTROL_BUTTON_EDIT,
	),
	[TabKeys.CONTROL_BUTTON_DELETE]: generateAccessSection(
		AccessKey.CONTROL_BUTTON_DELETE,
	),
	[TabKeys.SEARCH]: generateAccessSection(AccessKey.SEARCH),
	[TabKeys.FILTER]: generateAccessSection(AccessKey.FILTER),
	[TabKeys.LOWERS_STATISTICS_TABLE]: generateAccessSection(
		AccessKey.LOWERS_STATISTICS_TABLE,
	),
	//
	[TabKeys.COMPANIES_AND_TAXI_SERVICES]: generateAccessSectionFilter(
		AccessKey.COMPANIES_AND_TAXI_SERVICES,
	),
	[TabKeys.EXECUTOR_TEAMS]: generateAccessSectionFilter(
		AccessKey.EXECUTOR_TEAMS,
	),
	[TabKeys.PARKING_GROUP_LIST]: generateAccessSectionFilter(
		AccessKey.PARKING_GROUP_LIST,
	),
	[TabKeys.STATUS]: generateAccessSectionFilter(AccessKey.STATUS),
	[TabKeys.IS_WORKING]: generateAccessSectionFilter(AccessKey.IS_WORKING),
};

/**
 * Type representing the access names for each tab.
 */
type TabAccessNamesType = {
	[key in TabKeys]: string;
};

/**
 * Generates access names from arrays of access keys for each tab.
 * This mapping creates a unique access name for each tab, which is used
 * to determine whether the tab is accessible or not. Each access name represents
 * the combined access keys for the tab.
 *
 * @returns TabAccessNamesType - An object mapping each tab key to its unique access name
 */
const TabAccessNames: TabAccessNamesType = Object.fromEntries(
	Object.entries(ARR_ACCESS_PART_KEY).map(([key, section]) => [
		key,
		generateAccessName(...section),
	]),
) as TabAccessNamesType;

export { TabKeys, TabAccessNames, ARR_ACCESS_PART_KEY };
